import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { FormFooter } from '@atlaskit/form';
import { Box, Stack, Text } from '@atlaskit/primitives';
import InlineMessage from '@atlaskit/inline-message';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { CreditCardPaymentMethod, PaymentMethodPanel } from '@atlassian/commerce-payment-methods';
import { defaultBillingProfileSortOptions, maxThresholdForResults } from '@css-xp/ppc-constants';
import {
  NotificationTypes,
  successNotificationBuilder,
  useFormSubmit,
  useNotification,
  useSidebar,
  InfoPanel,
  ErrorComponents,
  AchPanel,
} from '@css-xp/base';
import { withApollo } from '@css-xp/base/graphql-provider';
import { deletePaymentMethodById } from '@atlassian/temp-proxies';
import { fetchBillingProfilesByPaymentMethodIdQuery } from '@css-xp/billing-v2';
import { AssociatedBillingProfiles } from '@css-xp/base/associated-billing-profiles';
import AssociatedBillingProfilesTable from './associated-billing-profiles-table';
import { CreditCard, BillingProfile, PayPal, Ach } from '@css-xp/graphtypes/src/generated-types-new';
import { PaypalPaymentMethod } from '@css-xp/ppc-paypal';
import './remove-payment-method.scss';

interface FormData {
  supportTicketLink: string;
  internalComment: string;
}

interface Props {
  refetch: () => Promise<void>;
  paymentMethod: Ach | CreditCard | PayPal;
  transactionAccountId: string;
  transactionAccountNumber: string;
  version?: number;
  isPayPalPaymentMethod?: boolean;
  payPalEmailId?: string;
  isACHPaymentMethod?: boolean;
}

const notificationOptions = successNotificationBuilder()
  .isVersion(NotificationTypes.FLAG)
  .withId('remove-payment-method-success')
  .withHeader('Update successful')
  .withMessage('Payment method sucessfully removed')
  .withDismissAfter(5000)
  .build();

export const RemovePaymentMethod = withApollo(
  ({
    refetch,
    paymentMethod,
    transactionAccountId,
    version,
    isPayPalPaymentMethod = false,
    payPalEmailId,
    isACHPaymentMethod = false,
  }: Props) => {
    const [showWarning, setShowWarning] = useState(false);
    const { error, isProcessing, submitForm, complete } = useFormSubmit({
      submitFormAction: deletePaymentMethodById,
    });

    // @ts-ignore
    const card = paymentMethod?.card;
    const paymentMethodId = paymentMethod?.id || '';
    const { closeSidebar } = useSidebar();

    const { data, loading } = useQuery(fetchBillingProfilesByPaymentMethodIdQuery, {
      variables: {
        id: paymentMethodId,
        searchOptions: {
          first: maxThresholdForResults,
          offset: 0,
          sort: defaultBillingProfileSortOptions,
        },
      },
    });

    let billingProfiles = data?.billingProfilesByPaymentMethodId?.billingProfiles || [];

    //filter active billing profiles
    billingProfiles = (billingProfiles as BillingProfile[]).filter(
      (billingProfile) => billingProfile?.isActive === true
    );

    const handleSubmit = (_formData: FormData) => {
      // _formData to be used when api supports metadata remove _ when it has to be used
      submitForm({ paymentMethodId, transactionAccountId });
    };

    const showWarningModal = () => setShowWarning(true);

    const closeWarning = () => setShowWarning(false);

    const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

    if (complete) {
      refetch();
      closeSidebar();
      if (!notificationSent) {
        sendSuccessNotification(notificationOptions);
        setNotificationSent(true);
      }
    }

    const cardDetails = {
      name: card?.cardHolderName,
      brand: card?.brand,
      expMonth: parseInt(card?.expirationMonth || ''),
      expYear: parseInt(card?.expirationYear || ''),
      last4: card?.last4Numbers,
    };

    const renderError = () => {
      if (error) {
        return (
          <div className="info-banners" data-testid="error-message">
            <InfoPanel
              type="error"
              message={
                <ErrorComponents.ErrorMessageOnInfoPanel
                  title="We're unable to delete the product data for the following reason"
                  message={error}
                />
              }
            />
          </div>
        );
      }
    };

    if (version === 2) {
      const hasMultipleBillingProfiles = billingProfiles?.length > 1;

      let paymentMethodPanel = null;

      if (isPayPalPaymentMethod) {
        paymentMethodPanel = (
          <PaypalPaymentMethod emailID={payPalEmailId} withBorder={true} renderChildrenInline={false} />
        );
      } else if (isACHPaymentMethod) {
        const ach = paymentMethod as Ach;
        paymentMethodPanel = <AchPanel showBorder bankName={ach?.ach?.bankName} last4={ach?.ach?.last4} />;
      } else {
        paymentMethodPanel = (
          <PaymentMethodPanel
            paymentMethod={
              {
                ...paymentMethod,
                card: cardDetails,
              } as CreditCardPaymentMethod
            }
          />
        );
      }

      const title = useMemo(() => {
        if (isPayPalPaymentMethod) {
          return 'Remove PayPal account';
        } else if (isACHPaymentMethod) {
          return 'Remove ACH account';
        }
        return 'Remove card';
      }, [isPayPalPaymentMethod, isACHPaymentMethod]);

      const warningMessage = useMemo(() => {
        if (isPayPalPaymentMethod) {
          return 'This PayPal account will be removed from your billing account.';
        } else if (isACHPaymentMethod) {
          return 'This ACH account will be removed from your billing account.';
        }

        return 'This card will be removed from your billing account.';
      }, [isPayPalPaymentMethod, isACHPaymentMethod]);

      const accountType = useMemo(() => {
        if (isPayPalPaymentMethod) {
          return 'PayPal account';
        } else if (isACHPaymentMethod) {
          return 'ACH account';
        }

        return 'card';
      }, [isPayPalPaymentMethod, isACHPaymentMethod]);

      return (
        <Box testId="remove-card-container-v2">
          <Stack space="space.200">
            <h2 className="title">{title}</h2>
            {!loading && hasMultipleBillingProfiles && (
              <InfoPanel
                type="warning"
                title={`This ${accountType} is used by multiple billing profiles`}
                message="To continue, first change the payment method for all active billing profiles that use this card."
              />
            )}
            {paymentMethodPanel}
            {!loading && hasMultipleBillingProfiles && (
              <Text weight="bold">Active billing profiles that use this {accountType}</Text>
            )}
            <AssociatedBillingProfiles
              isLoadingBillingProfiles={loading}
              billingProfiles={billingProfiles}
              txaId={transactionAccountId}
            />
            <ButtonGroup>
              <FormFooter>
                {hasMultipleBillingProfiles ? (
                  <Button appearance="primary" testId="close-button" onClick={() => closeSidebar()}>
                    Close
                  </Button>
                ) : (
                  <>
                    <Button appearance="subtle" testId="cancel-button" onClick={() => closeSidebar()}>
                      Cancel
                    </Button>
                    <Button
                      testId="remove-button"
                      onClick={showWarningModal}
                      appearance="primary"
                      isDisabled={!!billingProfiles.length || loading}
                    >
                      Confirm
                    </Button>
                  </>
                )}
              </FormFooter>
            </ButtonGroup>
          </Stack>
          <ModalTransition>
            {showWarning && (
              <Modal onClose={closeWarning} testId="remove-card-modal">
                <ModalHeader>
                  <ModalTitle>
                    <InlineMessage type="warning" secondaryText={title} />
                  </ModalTitle>
                </ModalHeader>
                <ModalBody>
                  {renderError()}
                  <p>{warningMessage}</p>
                </ModalBody>
                <ModalFooter>
                  <Form onSubmit={handleSubmit}>
                    {({ formProps }) => {
                      return (
                        <form {...formProps}>
                          <FormFooter align="end">
                            <ButtonGroup>
                              <Button appearance="subtle" testId="modal-cancel-button" onClick={closeWarning}>
                                Cancel
                              </Button>
                              <LoadingButton
                                testId="modal-confirm-button"
                                isLoading={isProcessing}
                                type="submit"
                                appearance="warning"
                                isDisabled={!!billingProfiles.length || loading}
                              >
                                Confirm
                              </LoadingButton>
                            </ButtonGroup>
                          </FormFooter>
                        </form>
                      );
                    }}
                  </Form>
                </ModalFooter>
              </Modal>
            )}
          </ModalTransition>
        </Box>
      );
    }

    return (
      <div className="remove-payment-method-sidebar-details">
        <h2 className="title">Remove payment method</h2>
        {renderError()}
        <AssociatedBillingProfilesTable
          paymentMethod={
            isPayPalPaymentMethod
              ? (paymentMethod as PayPal)
              : ({
                  ...paymentMethod,
                  card: cardDetails,
                  id: paymentMethod.id || '',
                } as CreditCardPaymentMethod)
          }
          billingProfiles={billingProfiles}
          isLoading={loading}
          transactionAccountId={transactionAccountId}
          isPayPalPaymentMethod={isPayPalPaymentMethod}
          payPalEmailId={payPalEmailId}
        />
        <Form onSubmit={handleSubmit}>
          {({ formProps }) => {
            return (
              <form {...formProps}>
                <FormFooter align="end">
                  <ButtonGroup>
                    <Button appearance="subtle" testId="cancel-button" onClick={() => closeSidebar()}>
                      Cancel
                    </Button>
                    <LoadingButton
                      testId="remove-button"
                      isLoading={isProcessing}
                      type="submit"
                      appearance="primary"
                      isDisabled={!!billingProfiles.length || loading}
                    >
                      Confirm
                    </LoadingButton>
                  </ButtonGroup>
                </FormFooter>
              </form>
            );
          }}
        </Form>
      </div>
    );
  }
);

export default RemovePaymentMethod;
